import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import TimerIcon from '@mui/icons-material/Timer';
import TimerOffIcon from '@mui/icons-material/TimerOff';

import { a11yProps, sizeTab, useTableFilter, hasSystemModule } from '@/shared';
import { SystemModulesEnum } from '@/auth/interfaces/system-modules.enum';
import { CustomTabPanel } from '@/shared/components/ui';

import {
  useFetchTicketEstados,
  useFetchTicketPrioridades,
  useFetchTicketAreas,
  useFetchTicketCanales,
  useFetchTicketTipos,
  useFetchTicketCategorias,
  useFetchTicketSubcategorias,
  useCreateCatalogItem,
  useUpdateCatalogItem,
  useDeleteCatalogItem,
  useSincronizarAreasDesdeErp,
  useFetchAreasPermitidas,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-catalogos.actions';
import { useFetchUsuarioResumen } from '@/shared/actions/erp/tickets-soporte-cliente';
import { useSincronizarInfraestructuraMasivos } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-masivos.actions';
import { useSincronizarContratosErp } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-clientes.actions';
import { useAuthStore } from '@/store/auth';
import {
  ITicketEstado,
  ITicketPrioridad,
  ITicketArea,
  ITicketCanal,
  ITicketTipo,
  ITicketCategoria,
  ITicketSubcategoria,
} from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';

import { useFetchSlaConfig, useToggleSlaConfig } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-configuracion.actions';
import TablaCatalogo, { TablaCatalogoColumn } from '../components/TablaCatalogo';
import FormCatalogo from '../components/FormCatalogo';
import ArbolCategorias from '../components/ArbolCategorias';
import SyncProgressDialog from '../components/SyncProgressDialog';
import PanelConfiguracionArbol from '../components/PanelConfiguracionArbol';
import PanelArbolCierre from '../components/PanelArbolCierre';
import PanelSlaArea from '../components/PanelSlaArea';
import PanelAdministracion from '../components/PanelAdministracion';

type CatalogType =
  | 'estados'
  | 'prioridades'
  | 'areas'
  | 'canales'
  | 'tipos'
  | 'categorias'
  | 'subcategorias'
  // Tab unificado que reemplaza a los 3 tabs Tipos/Categorias/Subcategorias
  // (eran la misma vista: el arbol completo editable en los 3 niveles).
  | 'arbol'
  | 'motivos_cierre';

const TAB_CONFIG: { label: string; catalogType: CatalogType }[] = [
  { label: 'Estados', catalogType: 'estados' },
  { label: 'Prioridades / SLA', catalogType: 'prioridades' },
  { label: 'Áreas', catalogType: 'areas' },
  { label: 'Canales', catalogType: 'canales' },
  // Tipos + Categorias + Subcategorias eran 3 tabs con la misma vista
  // (el arbol editable en los 3 niveles): unificados. La creacion por
  // nivel vive en los botones "+" de cada columna del arbol.
  { label: 'Árbol de Creación', catalogType: 'arbol' },
  { label: 'Árbol de Cierre', catalogType: 'motivos_cierre' },
];

const CATALOG_PK_FIELDS: Record<CatalogType, string> = {
  estados: 'id_ticket_estado',
  prioridades: 'id_ticket_prioridad',
  areas: 'id_area',
  canales: 'id_ticket_canal',
  tipos: 'id_ticket_tipo',
  categorias: 'id_ticket_categoria',
  subcategorias: 'id_ticket_subcategoria',
  // Sentinela: el tab 'arbol' no usa el CRUD generico de esta pagina.
  arbol: 'id_ticket_tipo',
  motivos_cierre: 'id_ticket_subcategoria',
};

// Módulo de sistema que gatea cada sub-tab (gestionable en Gestión de Accesos).
const CATALOG_TAB_MODULES: Record<CatalogType, SystemModulesEnum> = {
  estados: SystemModulesEnum.CATALOGOS_ESTADOS,
  prioridades: SystemModulesEnum.CATALOGOS_PRIORIDADES,
  areas: SystemModulesEnum.CATALOGOS_AREAS,
  canales: SystemModulesEnum.CATALOGOS_CANALES,
  tipos: SystemModulesEnum.CATALOGOS_TIPOS,
  categorias: SystemModulesEnum.CATALOGOS_CATEGORIAS,
  subcategorias: SystemModulesEnum.CATALOGOS_SUBCATEGORIAS,
  // El tab unificado se gatea por CUALQUIERA de los 3 modulos del arbol
  // (ver puedeVerTab); este valor es solo el representativo del Record.
  arbol: SystemModulesEnum.CATALOGOS_TIPOS,
  motivos_cierre: SystemModulesEnum.CATALOGOS_ARBOL_CIERRE,
};

const formatSla = (minutos: number): string => {
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const min = minutos % 60;
  if (horas < 24) return min > 0 ? `${horas}h ${min}m` : `${horas}h`;
  const dias = Math.floor(horas / 24);
  const horasRestantes = horas % 24;
  if (horasRestantes > 0) return `${dias}d ${horasRestantes}h`;
  return `${dias}d`;
};

const CatalogosPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ item: any; name: string } | null>(null);

  const { pagination, setPagination } = useTableFilter();

  // Sub-tabs visibles según los módulos de sistema del usuario.
  // Retrocompat: si el usuario NO tiene ningún módulo TICKETS_CATALOGOS.*
  // asignado, ve TODOS los sub-tabs (comportamiento previo → NO rompe lo ya
  // configurado). Si tiene al menos uno, se aplica gateo estricto por módulo.
  // (hasSystemModule ya deja pasar a superusuario en todos.)
  // Los modulos TIPOS/CATEGORIAS/SUBCATEGORIAS siguen existiendo en
  // Gestion de Accesos: cualquiera de los 3 habilita el tab unificado
  // "Arbol de Creacion" (y cuentan para la retrocompat).
  const MODULOS_ARBOL = [
    SystemModulesEnum.CATALOGOS_TIPOS,
    SystemModulesEnum.CATALOGOS_CATEGORIAS,
    SystemModulesEnum.CATALOGOS_SUBCATEGORIAS,
  ];
  const hasAnyCatalogoModule =
    TAB_CONFIG.some((t) => hasSystemModule(CATALOG_TAB_MODULES[t.catalogType])) ||
    MODULOS_ARBOL.some((m) => hasSystemModule(m));
  const puedeVerTab = (ct: CatalogType) =>
    ct === 'arbol'
      ? MODULOS_ARBOL.some((m) => hasSystemModule(m)) || !hasAnyCatalogoModule
      : hasSystemModule(CATALOG_TAB_MODULES[ct]) || !hasAnyCatalogoModule;
  const visibleTabs = TAB_CONFIG.filter((t) => puedeVerTab(t.catalogType));

  // tabValue indexa sobre visibleTabs (no sobre TAB_CONFIG). Los tabs dinámicos
  // "Configuración Árbol"/"Administración" viven después (visibleTabs.length…);
  // ahí no hay catalogType real → 'estados' como sentinela para que las hooks de
  // mutations no rompan (esos paneles no usan mutations).
  const currentCatalogType: CatalogType =
    visibleTabs[tabValue]?.catalogType ?? 'estados';
  // El tipo 'motivos_cierre' no existe en la API de mutations: internamente
  // son subcategorias. PanelMotivosCierre maneja sus propios mutations, asi
  // que aqui mapeamos a 'subcategorias' como fallback inerte.
  const mutationCatalogType: any =
    currentCatalogType === 'motivos_cierre' || currentCatalogType === 'arbol'
      ? 'subcategorias'
      : currentCatalogType;
  const createMutation = useCreateCatalogItem(mutationCatalogType);
  const updateMutation = useUpdateCatalogItem(mutationCatalogType);
  const deleteMutation = useDeleteCatalogItem(mutationCatalogType);
  const sincronizarAreas = useSincronizarAreasDesdeErp();
  const sincronizarInfraMasivos = useSincronizarInfraestructuraMasivos();
  const sincronizarContratosErp = useSincronizarContratosErp();

  const { data: slaConfigRes } = useFetchSlaConfig();
  const toggleSlaMutation = useToggleSlaConfig();
  const slaEnabled: boolean = (slaConfigRes as any)?.data?.sla_habilitado ?? true;

  const { data: estadosRes, isLoading: loadingEstados, isRefetching: refetchingEstados } =
    useFetchTicketEstados();
  const { data: prioridadesRes, isLoading: loadingPrioridades, isRefetching: refetchingPrioridades } =
    useFetchTicketPrioridades();
  const { data: areasRes, isLoading: loadingAreas, isRefetching: refetchingAreas } =
    useFetchTicketAreas();
  const { data: canalesRes, isLoading: loadingCanales, isRefetching: refetchingCanales } =
    useFetchTicketCanales();
  // Filtro de area para la tabla de Tipos. null = "todas las areas"
  // (default para admin global, o usuario sin areas asignadas).
  const [tiposAreaFilter, setTiposAreaFilter] = useState<string | null>(null);
  const { data: tiposRes } =
    useFetchTicketTipos({ areaId: tiposAreaFilter || undefined });

  // Areas que el usuario puede gestionar / asignar a Tipos:
  //   - Admin global (is_superuser): todas las areas
  //   - Supervisor (es_responsable=true en api2_usuario_area): solo las suyas
  //   - Configurador (es_configuracion=true, check "Configuración" en
  //     Gestión de Accesos): solo las suyas — puede editar el arbol sin ser
  //     responsable ni recibir tickets del area (caso admin TI)
  // El frontend usa esta lista para popular tanto el filtro de la tabla
  // como el selector de area en el form de crear/editar Tipo.
  const authUser = useAuthStore(s => s.user);
  const isSuperUser = !!(authUser as any)?.is_superuser;
  // Permisos para los 3 botones de sincronizacion. Cada uno se gatea por
  // su permiso especifico (migracion V1_12_0). Retrocompat: si nadie tiene
  // el permiso fino, caemos al paraguas tickets.gestionar_catalogos /
  // tickets.gestionar_masivos / admin.sincronizar_datos para no romper.
  const currentUserId = (authUser as any)?.user_id || (authUser as any)?.user || 0;
  const { data: _resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = _resumenRes as any;
  const userPermisos: string[] =
    _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  const tieneFino = (prefix: string) => userPermisos.some(p => p.startsWith(prefix));
  const can = (perm: string, fallback?: string) => {
    if (isSuperUser) return true;
    if (userPermisos.includes(perm)) return true;
    // Si el modulo fino no existe aun para este user, cae al fallback.
    if (fallback && !tieneFino(perm.split('.')[0] + '.') && userPermisos.includes(fallback)) {
      return true;
    }
    return false;
  };
  const canSincronizarAreas = can('catalogos.sincronizar_areas', 'admin.sincronizar_datos');
  const canSincronizarInfra = can('masivos.sincronizar_infraestructura', 'tickets.gestionar_masivos');
  const canSincronizarContratosErp = can('catalogos.sincronizar_contratos_erp', 'admin.sincronizar_datos');
  // Tab "Configuracion Arbol": panel admin global para toggle "incluir
  // legacy" por area + lista de tipos legacy. Solo visible con el permiso
  // catalogos.toggle_legacy_por_area (migracion V1_14_0).
  const canConfigurarArbol = isSuperUser || userPermisos.includes('catalogos.toggle_legacy_por_area');
  // Tab "Administración": ahora específico por usuario vía módulo de sistema
  // TICKETS_CATALOGOS.ADMINISTRACION (gestionable en Gestión de Accesos).
  // Superusuario siempre lo ve; para el resto requiere el módulo asignado.
  const canAdministracion = isSuperUser || hasSystemModule(SystemModulesEnum.CATALOGOS_ADMINISTRACION);
  const { data: areasPermitidasRes } = useFetchAreasPermitidas();
  const areasPermitidas: ITicketArea[] = (areasPermitidasRes as any)?.data ?? [];

  // Para supervisor (no super): el filtro de area arranca apuntando a su
  // propia area y queda bloqueado en ella. Asi solo ve y edita su arbol.
  // El admin global sigue con "todas" por default.
  useEffect(() => {
    if (isSuperUser) return;
    if (tiposAreaFilter) return;
    if (areasPermitidas.length === 0) return;
    setTiposAreaFilter(areasPermitidas[0].id_area);
  }, [isSuperUser, areasPermitidas, tiposAreaFilter]);
  // Categorias y subcategorias se filtran por el mismo area que Tipos:
  // - Supervisor: su area (auto). Solo ve categorias/subcategorias de su arbol.
  // - Admin global: por default "todas", o el area que tenga seleccionada.
  const { data: categoriasRes } = useFetchTicketCategorias(
    undefined,
    undefined,
    tiposAreaFilter || undefined,
  );
  const { data: subcategoriasRes } = useFetchTicketSubcategorias(
    undefined,
    undefined,
    tiposAreaFilter || undefined,
  );

  // erpApi.get() ya unwrappea axios.data, asi que `xxxRes` ES el body del
  // response: { code, status, data: [...], total, page, page_size, pages }.
  // Los rows viven en `xxxRes.data` directamente, sin nivel extra.
  const estados: ITicketEstado[] = (estadosRes as any)?.data ?? [];
  const prioridades: ITicketPrioridad[] = (prioridadesRes as any)?.data ?? [];
  const areas: ITicketArea[] = (areasRes as any)?.data ?? [];
  const canales: ITicketCanal[] = (canalesRes as any)?.data ?? [];
  const tipos: ITicketTipo[] = (tiposRes as any)?.data ?? [];

  // Lista de areas que el form de crear/editar Tipo ofrece como opciones.
  // Admin ve todas (areas); supervisor solo las suyas (areasPermitidas).
  const areasEditables: ITicketArea[] = isSuperUser ? areas : areasPermitidas;
  const categorias: ITicketCategoria[] = (categoriasRes as any)?.data ?? [];
  // Reservado para proxima pantalla de subcategorias — referenciado con
  // underscore para silenciar TS6133 sin perder el fetch.
  const _subcategorias: ITicketSubcategoria[] = (subcategoriasRes as any)?.data ?? [];
  void _subcategorias;

  const renderColorCell = (value: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: value || '#ccc',
          border: '1px solid #ddd',
        }}
      />
      <Typography variant="body2">{value}</Typography>
    </Box>
  );

  const renderBooleanCell = (value: boolean) => (
    <Chip
      label={value ? 'Si' : 'No'}
      size="small"
      color={value ? 'success' : 'default'}
      variant="outlined"
    />
  );

  const columnsEstados: TablaCatalogoColumn[] = useMemo(
    () => [
      { accessorKey: 'codigo', header: 'Codigo', size: 120 },
      { accessorKey: 'nombre', header: 'Nombre', size: 180 },
      {
        accessorKey: 'color',
        header: 'Color',
        size: 140,
        Cell: ({ row }: { row: any }) => renderColorCell(row.original.color),
      },
      { accessorKey: 'icono', header: 'Icono', size: 100 },
      { accessorKey: 'orden', header: 'Orden', size: 80 },
      {
        accessorKey: 'es_inicial',
        header: 'Es Inicial',
        size: 100,
        Cell: ({ row }: { row: any }) => renderBooleanCell(row.original.es_inicial),
      },
      {
        accessorKey: 'es_final',
        header: 'Es Final',
        size: 100,
        Cell: ({ row }: { row: any }) => renderBooleanCell(row.original.es_final),
      },
      {
        accessorKey: 'activo',
        header: 'Activo',
        size: 100,
        Cell: ({ row }: { row: any }) => renderBooleanCell(row.original.activo),
      },
    ],
    []
  );

  const columnsPrioridades: TablaCatalogoColumn[] = useMemo(
    () => {
      const cols: TablaCatalogoColumn[] = [
        { accessorKey: 'codigo', header: 'Codigo', size: 120 },
        { accessorKey: 'nombre', header: 'Nombre', size: 180 },
      ];
      if (slaEnabled) {
        cols.push({
          accessorKey: 'sla_minutos',
          header: 'SLA',
          size: 150,
          Cell: ({ row }: { row: any }) => {
            const min = row.original.sla_minutos;
            return (
              <Box>
                <Typography variant="body2" fontWeight={600}>{formatSla(min)}</Typography>
                <Typography variant="caption" color="text.secondary">{min} min</Typography>
              </Box>
            );
          },
        });
      }
      cols.push(
        {
          accessorKey: 'color',
          header: 'Color',
          size: 140,
          Cell: ({ row }: { row: any }) => renderColorCell(row.original.color),
        },
        { accessorKey: 'icono', header: 'Icono', size: 100 },
        { accessorKey: 'orden', header: 'Orden', size: 80 },
      );
      return cols;
    },
    [slaEnabled]
  );

  const columnsAreas: TablaCatalogoColumn[] = useMemo(
    () => [
      { accessorKey: 'codigo', header: 'Codigo', size: 120 },
      { accessorKey: 'nombre', header: 'Nombre', size: 180 },
      { accessorKey: 'descripcion', header: 'Descripcion', size: 200 },
      {
        accessorKey: 'color',
        header: 'Color',
        size: 140,
        Cell: ({ row }: { row: any }) => renderColorCell(row.original.color),
      },
      { accessorKey: 'orden', header: 'Orden', size: 80 },
      {
        accessorKey: 'activa',
        header: 'Activa',
        size: 100,
        Cell: ({ row }: { row: any }) => renderBooleanCell(row.original.activa),
      },
    ],
    []
  );

  const columnsCanales: TablaCatalogoColumn[] = useMemo(
    () => [
      { accessorKey: 'codigo', header: 'Codigo', size: 120 },
      { accessorKey: 'nombre', header: 'Nombre', size: 200 },
      { accessorKey: 'icono', header: 'Icono', size: 120 },
      {
        accessorKey: 'activo',
        header: 'Activo',
        size: 100,
        Cell: ({ row }: { row: any }) => renderBooleanCell(row.original.activo),
      },
    ],
    []
  );

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingItem(null);
  };

  const handleFormSubmit = (data: any) => {
    const pkField = CATALOG_PK_FIELDS[currentCatalogType];
    const isEditing = !!editingItem;

    if (isEditing) {
      const id = editingItem[pkField];
      updateMutation.mutate(
        { id, data },
        { onSuccess: () => handleCloseForm() }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => handleCloseForm(),
      });
    }
  };

  const handleDeleteRequest = (item: any) => {
    setDeleteConfirm({ item, name: item.nombre || item.codigo || 'este elemento' });
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) return;
    const pkField = CATALOG_PK_FIELDS[currentCatalogType];
    const id = deleteConfirm.item[pkField];
    deleteMutation.mutate(id, {
      onSuccess: () => setDeleteConfirm(null),
      onError: () => setDeleteConfirm(null),
    });
  };

  const getTableConfig = (ct: CatalogType) => {
    switch (ct) {
      case 'estados':
        return {
          data: estados,
          columns: columnsEstados,
          isLoading: loadingEstados,
          isRefetching: refetchingEstados,
        };
      case 'prioridades':
        return {
          data: prioridades,
          columns: columnsPrioridades,
          isLoading: loadingPrioridades,
          isRefetching: refetchingPrioridades,
        };
      case 'areas':
        return {
          data: areas,
          columns: columnsAreas,
          isLoading: loadingAreas,
          isRefetching: refetchingAreas,
        };
      case 'canales':
        return {
          data: canales,
          columns: columnsCanales,
          isLoading: loadingCanales,
          isRefetching: refetchingCanales,
        };
      default:
        return { data: [], columns: [], isLoading: false, isRefetching: false };
    }
  };

  return (
    <Paper elevation={2} sx={{ padding: 2, mb: 2, borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h4"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <SettingsIcon color="primary" />
          ADMINISTRACION DE CATALOGOS
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Boton "Sincronizar desde ERP": solo visible en el tab de Areas
              (index 2). Dispara POST /catalogo/areas/sincronizar-erp/ que
              lee api_departamento de base_erp y hace UPSERT en api2_area. */}
          {currentCatalogType === 'areas' && canSincronizarAreas && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={() => sincronizarAreas.mutate()}
              disabled={sincronizarAreas.isPending}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {sincronizarAreas.isPending ? 'Sincronizando...' : 'Sincronizar desde ERP'}
            </Button>
          )}
          {/* Sincroniza OLT/Caja Primaria/NAP/Router desde base_erp (read-only)
              hacia las tablas locales que alimentan el formulario de daños
              masivos. Visible siempre porque es una accion administrativa
              global, no atada a un tab. */}
          {canSincronizarInfra && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={() => sincronizarInfraMasivos.start()}
              disabled={sincronizarInfraMasivos.isStarting || sincronizarInfraMasivos.isActive}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {sincronizarInfraMasivos.isActive
                ? `Sincronizando... ${sincronizarInfraMasivos.status?.percent ?? 0}%`
                : 'Sincronizar Infraestructura'}
            </Button>
          )}
          {/* Promueve contratos huerfanos (existen en tickets_contrato pero
              no en api_contrato del ERP). Idempotente: si el contrato ya
              esta en Django, NO se sobreescribe. Necesario para que los
              flujos de visita/traslado encuentren el contrato en el ERP
              cuando el cliente fue importado desde Softnet pero nunca
              promovido al ERP. */}
          {canSincronizarContratosErp && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={() => sincronizarContratosErp.start()}
              disabled={sincronizarContratosErp.isStarting || sincronizarContratosErp.isActive}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              {sincronizarContratosErp.isActive
                ? `Sincronizando... ${sincronizarContratosErp.status?.percent ?? 0}%`
                : 'Sincronizar Contratos a EVA'}
            </Button>
          )}
          {/* En el tab unificado "Arbol de Creacion" la creacion es por
              columna (boton "+" en Tipos/Categorias/Subcategorias), asi
              que el "Agregar" generico se oculta ahi. */}
          {currentCatalogType !== 'arbol' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Agregar
            </Button>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Tabs */}
      <Box sx={{ bgcolor: 'background.paper', maxWidth: sizeTab }}>
        <Tabs
          value={tabValue}
          onChange={(_e: React.SyntheticEvent, newValue: number) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="catalogos tabs"
        >
          {visibleTabs.map((tab, idx) => (
            <Tab key={tab.catalogType} label={tab.label} {...a11yProps(idx)} />
          ))}
          {canConfigurarArbol && (
            <Tab
              key="configuracion-arbol"
              label="Configuración Árbol"
              {...a11yProps(visibleTabs.length)}
            />
          )}
          {canAdministracion && (
            <Tab
              key="administracion"
              label="Administración"
              {...a11yProps(visibleTabs.length + (canConfigurarArbol ? 1 : 0))}
            />
          )}
        </Tabs>
      </Box>

      {/* Tab "Configuración Árbol" — panel admin global. Render aparte
          porque NO es un CRUD generico de TAB_CONFIG. */}
      {canConfigurarArbol && (
        <CustomTabPanel value={tabValue} index={visibleTabs.length}>
          <Box sx={{ mt: 2 }}>
            <PanelConfiguracionArbol />
          </Box>
        </CustomTabPanel>
      )}

      {/* Tab "Administración" — parámetros de configuración (ej. descuento por
          traslado con FibraPoints). Render aparte, no es CRUD de TAB_CONFIG. */}
      {canAdministracion && (
        <CustomTabPanel
          value={tabValue}
          index={visibleTabs.length + (canConfigurarArbol ? 1 : 0)}
        >
          <Box sx={{ mt: 2 }}>
            <PanelAdministracion />
          </Box>
        </CustomTabPanel>
      )}

      {visibleTabs.map((tab, idx) => {
        if (tab.catalogType === 'arbol') {
          return (
            <CustomTabPanel key={tab.catalogType} value={tabValue} index={idx}>
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  {/* Pasamos el mismo tiposAreaFilter para que el arbol
                      Tipo/Categoria/Subcategoria respete la seleccion
                      de area. Supervisor: su area; Admin: lo que elija. */}
                  <ArbolCategorias
                    areaId={tiposAreaFilter}
                    areasEditables={areasEditables}
                    isSuperUser={isSuperUser}
                  />
                </Box>
              </Grid>
            </CustomTabPanel>
          );
        }
        if (tab.catalogType === 'motivos_cierre') {
          return (
            <CustomTabPanel key={tab.catalogType} value={tabValue} index={idx}>
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <PanelArbolCierre
                    areasEditables={areasEditables}
                    areaFilter={tiposAreaFilter}
                    isSuperUser={isSuperUser}
                  />
                </Box>
              </Grid>
            </CustomTabPanel>
          );
        }

        const config = getTableConfig(tab.catalogType);
        return (
          <CustomTabPanel key={tab.catalogType} value={tabValue} index={idx}>
            <Grid item xs={12}>
              {/* Toggle SLA Global — solo en pestaña Prioridades/SLA */}
              {tab.catalogType === 'prioridades' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 1,
                    mb: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: slaEnabled ? 'rgba(46,125,50,0.08)' : 'rgba(0,0,0,0.04)',
                    border: '1px solid',
                    borderColor: slaEnabled ? 'success.light' : 'divider',
                  }}
                >
                  {slaEnabled ? (
                    <TimerIcon color="success" />
                  ) : (
                    <TimerOffIcon color="disabled" />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      SLA Global
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {slaEnabled
                        ? 'El SLA está activo. Se calculan plazos y se envían notificaciones al vencer.'
                        : 'El SLA está desactivado. No se calculan plazos ni se envían notificaciones.'}
                    </Typography>
                  </Box>
                  <Switch
                    checked={slaEnabled}
                    onChange={() => toggleSlaMutation.mutate(!slaEnabled)}
                    disabled={toggleSlaMutation.isPending}
                    color="success"
                  />
                </Box>
              )}

              {/* SLA por Área — cada área define su propio SLA (admin tipo Árboles) */}
              {tab.catalogType === 'prioridades' && areasEditables.length > 0 && (
                <PanelSlaArea
                  areasEditables={areasEditables}
                  isSuperUser={isSuperUser}
                  slaGlobalEnabled={slaEnabled}
                />
              )}

              <Box sx={{ mt: 2 }}>
                <TablaCatalogo
                  data={config.data}
                  columns={config.columns}
                  isLoading={config.isLoading}
                  isRefetching={config.isRefetching}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                  pagination={pagination}
                  onPaging={setPagination}
                  rowCount={config.data.length}
                />
              </Box>
            </Grid>
          </CustomTabPanel>
        );
      })}

      <FormCatalogo
        open={formOpen}
        onClose={handleCloseForm}
        // FormCatalogo no maneja 'motivos_cierre' (su tab usa PanelMotivosCierre
        // con su propio modal). Cuando el tab activo es ese, mapeamos a
        // 'subcategorias' para satisfacer el contrato de tipos sin afectar UX
        // — el modal queda cerrado en esa pestaña.
        catalogType={mutationCatalogType}
        initialData={editingItem}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        tipos={tipos}
        categorias={categorias}
        areas={areasEditables}
      />

      {/* Confirmacion de eliminacion */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
      >
        <DialogTitle>Confirmar eliminacion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estas seguro de que deseas eliminar <strong>{deleteConfirm?.name}</strong>? Esta accion no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit" disabled={deleteMutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de progreso para Sincronizar Infraestructura. Aparece cuando
          jobId existe; lo cerramos solo al terminar para que el user vea el
          resumen. resetJob limpia el id local para permitir disparar otra vez. */}
      <SyncProgressDialog
        open={!!sincronizarInfraMasivos.jobId}
        titulo="Sincronizar Infraestructura (OLT / Troncal / NAP / Router)"
        status={sincronizarInfraMasivos.status}
        onClose={() => sincronizarInfraMasivos.resetJob()}
      />

      {/* Dialog de progreso para Sincronizar Contratos a EVA */}
      <SyncProgressDialog
        open={!!sincronizarContratosErp.jobId}
        titulo="Sincronizar Contratos a EVA"
        status={sincronizarContratosErp.status}
        onClose={() => sincronizarContratosErp.resetJob()}
      />
    </Paper>
  );
};

export default CatalogosPage;
